/**
 * Persona-aware navigation to settlement rails (comms vs rail origins).
 *
 * Model:
 *   kgnex.com     — listings, chat, storefronts, signup (communications)
 *   settle.kgnex.com — DePay / widget escrow (R2)
 *   gig.kgnex.com — noticeboard gig settlement (R4): merchant + runner personas
 *   chain.kgnex.com — UTT / cross-chain (R3/R5)
 */
import { isCommsOnly } from './commsMode';
import { ECOSYSTEM_ORIGINS } from './ecosystemOrigins';

export const RAIL_PERSONAS = {
  merchant: 'merchant',
  runner: 'runner',
};

/** Apps whose operational UI lives on the gig rail when comms-only. */
export const GIG_RAIL_APP_IDS = new Set(['runner', 'merchant', 'xcourier', 'xproxy']);

export const isExternalHref = href => /^https?:\/\//i.test(href);

/**
 * @param {'merchant'|'runner'} persona
 * @param {string} [subPath] e.g. `/pms/store-id` (no leading persona prefix)
 */
export const railPersonaUrl = (persona, subPath = '') => {
  const base = ECOSYSTEM_ORIGINS.gig.replace(/\/$/, '');
  const suffix = subPath ? (subPath.startsWith('/') ? subPath : `/${subPath}`) : '';
  return `${base}/${persona}${suffix}`;
};

/** Neutral gig funding entry from a trade-intent handoff in chat. */
export const gigTradeIntentUrl = tradeIntentId => {
  const base = ECOSYSTEM_ORIGINS.gig.replace(/\/$/, '');
  return `${base}/start?tradeIntentId=${encodeURIComponent(tradeIntentId)}`;
};

/**
 * Resolve app launcher / hub "go live" target.
 */
export const resolveBxAppLaunchHref = app => {
  if (!isCommsOnly()) {
    return app.launchHref;
  }

  switch (app.id) {
    case 'runner':
    case 'xcourier':
    case 'xproxy':
      return railPersonaUrl(RAIL_PERSONAS.runner);
    case 'merchant':
      return railPersonaUrl(RAIL_PERSONAS.merchant);
    case 'xchange':
    case 'xbuy':
    case 'xstays':
      return ECOSYSTEM_ORIGINS.depay;
    default:
      return app.launchHref;
  }
};

/**
 * Tile href for Apps launcher — guided hubs stay on comms except gig-rail apps.
 */
export const resolveAppTileHref = app => {
  if (isCommsOnly() && GIG_RAIL_APP_IDS.has(app.id)) {
    return resolveBxAppLaunchHref(app);
  }
  return app.hubPath;
};

export default {
  railPersonaUrl,
  gigTradeIntentUrl,
  resolveBxAppLaunchHref,
  resolveAppTileHref,
  isExternalHref,
  GIG_RAIL_APP_IDS,
};
